﻿let _profUserId = 'default'; try { require('@react-native-async-storage/async-storage').default.getItem('userToken').then(function(t){if(t)_profUserId=t.slice(0,16);}).catch(function(){}); } catch(e) {}
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  TextInput,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  ScrollView,
  Dimensions,
  Image,
  ActivityIndicator,
  StatusBar,
  RefreshControl,
  Animated,
  Alert,
  Modal,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome5,
  MaterialIcons,
} from '@expo/vector-icons';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';

// Import the fixed activity tracker
import activityTracker from '../../services/UnifiedActivityTracker';
import { apiFetch } from '../../utils/apiClient';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

// === API CONFIG ===
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const { width, height } = Dimensions.get('window');

// JOBENG Logo component using fallback
const JobengLogo = () => (
  <View style={styles.logoContainer}>
    <Text style={styles.logoText}>JOBENG</Text>
  </View>
);

export default function AllActivityScreen({ navigation, route }) {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  // === Live API Data ===
  const [userId, setUserId] = React.useState('default');
  React.useEffect(() => {
    AsyncStorage.getItem('userToken').then(t => { if (t) setUserId(t.slice(0, 16)); }).catch(() => {});
  }, []);
  React.useEffect(() => {
    if (userId === 'default') return;
    const _ck = `professional_${_profUserId}_allactivityscreen`;
    apiFetch('/api/data/applications?sort=created_at&order=desc&limit=50').then(d => {
      if (d && !d.error) {
        const arr = Array.isArray(d) ? d : (d.data || d.results || []);
        AsyncStorage.setItem(_ck, JSON.stringify(arr)).catch(() => {});
      }
    }).catch(() => {});
  }, [userId]);

  // State management
  const [activities, setActivities] = useState([]);
  const [filteredActivities, setFilteredActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedPriority, setSelectedPriority] = useState('all');
  const [selectedDateRange, setSelectedDateRange] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [statistics, setStatistics] = useState({});
  const [sortBy, setSortBy] = useState('newest');
  const [initializationError, setInitializationError] = useState(null);

  // Animation values
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(50)).current;

  // Filter options
  const categories = [
    { key: 'all', label: 'All Activities', icon: 'apps', color: '#6B7280' },
    { key: 'application', label: 'Applications', icon: 'paper-plane', color: '#10B981' },
    { key: 'interview', label: 'Interviews', icon: 'calendar', color: '#3B82F6' },
    { key: 'offer', label: 'Offers', icon: 'gift', color: '#F59E0B' },
    { key: 'portfolio', label: 'Portfolio', icon: 'briefcase', color: '#8B5CF6' },
    { key: 'assessment', label: 'Assessments', icon: 'school', color: '#06B6D4' },
    { key: 'learning', label: 'Learning', icon: 'book', color: '#10B981' },
  ];

  const statusOptions = [
    { key: 'all', label: 'All Status', color: '#6B7280' },
    { key: 'completed', label: 'Completed', color: '#10B981' },
    { key: 'pending', label: 'Pending', color: '#F59E0B' },
    { key: 'scheduled', label: 'Scheduled', color: '#3B82F6' },
    { key: 'cancelled', label: 'Cancelled', color: '#EF4444' },
  ];

  const priorityOptions = [
    { key: 'all', label: 'All Priority', color: '#6B7280' },
    { key: 'high', label: 'High Priority', color: '#EF4444' },
    { key: 'medium', label: 'Medium Priority', color: '#F59E0B' },
    { key: 'low', label: 'Low Priority', color: '#10B981' },
  ];

  const dateRangeOptions = [
    { key: 'all', label: 'All Time' },
    { key: 'today', label: 'Today' },
    { key: 'week', label: 'This Week' },
    { key: 'month', label: 'This Month' },
    { key: 'quarter', label: 'Last 3 Months' },
  ];

  const sortOptions = [
    { key: 'newest', label: 'Newest First' },
    { key: 'oldest', label: 'Oldest First' },
    { key: 'priority', label: 'Priority' },
    { key: 'category', label: 'Category' },
  ];

  // Initialize component
  useEffect(() => {
    initializeData();
    startAnimations();
  }, []);

  // Focus effect to refresh data when screen is focused
  useFocusEffect(
    useCallback(() => {
      if (activityTracker.initialized) {
        loadActivities();
      }
    }, [])
  );

  // Initialize activity tracker and load data
  const initializeData = async () => {
    try {
      console.log('🔄 Initializing AllActivityScreen...');
      setLoading(true);
      setInitializationError(null);

      // Initialize activity tracker with error handling
      const initialized = await activityTracker.initialize();
      
      if (!initialized) {
        throw new Error('Failed to initialize activity tracker');
      }

      // Load activities and statistics
      await loadActivities();
      
      console.log('✅ AllActivityScreen initialized successfully');
    } catch (error) {
      console.error('❌ Error initializing AllActivityScreen:', error);
      setInitializationError(error.message);
      
      // Show user-friendly error message
      Alert.alert(
        'Initialization Error', 
        'There was an issue loading your activities. The app will continue with limited functionality.',
        [
          { text: 'Retry', onPress: initializeData },
          { text: 'Continue', style: 'cancel' }
        ]
      );
    } finally {
      setLoading(false);
    }
  };

  // Load activities from tracker
  const loadActivities = async () => {
    try {
      console.log('[AllActivity] loading from DB + activityTracker...');

      // 1. REAL DB activities — applications, interviews, offers, saved jobs
      const dbActs = await fetchDbActivities();

      // 2. App-internal events from activityTracker (portfolio updates, skill
      //    assessments, webinar registrations — things that don't have a
      //    canonical DB table yet)
      const filters = {
        category: selectedCategory,
        status:   selectedStatus,
        priority: selectedPriority,
        dateRange: selectedDateRange,
        search:   searchQuery,
      };
      const trackerActs = (activityTracker.getActivities(filters) || [])
        // Filter out activity types we now source from the DB, so we don't
        // get duplicates (e.g. mock 'offer_received' alongside real ones).
        .filter(a => !DB_SOURCED_TYPES.has(a.type));

      // 3. Merge, de-dupe by composite key, sort newest-first
      const all = [...dbActs, ...trackerActs];
      const seen = new Set();
      const merged = all.filter(a => {
        const key = a.id || `${a.type}_${a.entityId || ''}_${a.date || ''}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      const sorted = sortActivities(merged, sortBy);
      const stats  = activityTracker.getStatistics();

      setActivities(merged);
      setFilteredActivities(sorted);
      setStatistics({ ...stats, total_activities: merged.length, db_activities: dbActs.length });

      console.log(`[AllActivity] ✅ ${merged.length} total (${dbActs.length} from DB)`);
    } catch (error) {
      console.error('[AllActivity] load failed:', error);
    }
  };

  // Activity types now sourced exclusively from the database. The mock
  // activityTracker still emits these, so we filter them out to avoid the
  // 'job_002 — Frontend Engineer @ StartupHub Manchester' fake activity
  // showing up next to a real one.
  const DB_SOURCED_TYPES = new Set([
    'application_submitted',
    'application_rejected',
    'application_status_change',
    'interview_scheduled',
    'interview_completed',
    'offer_received',
    'offer_accepted',
    'offer_declined',
    'job_saved',
  ]);

  // Resolve the professional's id + email from the same multi-key path
  // used by JobsOffersScreen / dashboard so the DB queries hit the right
  // rows. Returns {id, email} or {null, null} when unresolvable.
  const resolveIdentifiers = async () => {
    const candidateKeys = ['user_data', 'user_profile', 'auth_user', 'userData', 'currentUser'];
    let id = null, email = null;
    for (const k of candidateKeys) {
      try {
        const raw = await AsyncStorage.getItem(k);
        if (!raw) continue;
        const parsed = JSON.parse(raw);
        const u = parsed?.user || parsed;
        if (!id    && (u?.id || u?.user_id))      id    = u.id || u.user_id;
        if (!email && u?.email)                   email = u.email;
        if (id && email) break;
      } catch {}
    }
    if (!email) email = await AsyncStorage.getItem('user_email').catch(() => null);
    if (!id)    id    = await AsyncStorage.getItem('user_id').catch(() => null);
    return { id, email };
  };

  // Pull real activities from the database. Returns a unified shape that
  // matches what activityTracker emits, so the existing card render works.
  const fetchDbActivities = async () => {
    const { id: userId, email: userEmail } = await resolveIdentifiers();
    if (!userId && !userEmail) {
      console.log('[AllActivity] fetchDbActivities: no identifier — skipping');
      return [];
    }
    console.log('[AllActivity] fetchDbActivities for', { userId, userEmail });

    const activities = [];
    const fmtDate = (d) => d ? new Date(d).toISOString() : new Date().toISOString();

    // ── 1. APPLICATIONS ───────────────────────────────────────────────
    try {
      const url = `/api/data/applications?candidate_email=${encodeURIComponent(userEmail || '')}&limit=100`;
      const r = await apiFetch(url);
      const rows = Array.isArray(r) ? r : (r?.data || []);
      for (const app of rows) {
        activities.push({
          id:           `db_app_${app.id}`,
          type:         'application_submitted',
          title:        `Applied to ${app.current_title || app.position || 'a role'}`,
          subtitle:     app.current_company || app.employer_name || '',
          description:  `Application ${app.application_id || '#' + app.id} — stage: ${app.stage || 'submitted'}`,
          date:         fmtDate(app.created_at || app.applied_date),
          category:     'applications',
          priority:     'normal',
          status:       app.status || 'active',
          jobId:        app.job_id,
          applicationId: app.id,
          entityId:     app.id,
          icon:         'paper-plane',
          color:        '#3B82F6',
          metadata:     app,
        });
        // Stage-change derived activity when stage is past 'submitted'
        if (app.stage && app.stage !== 'submitted' && app.stage !== 'new') {
          activities.push({
            id:           `db_app_stage_${app.id}_${app.stage}`,
            type:         'application_status_change',
            title:        `${app.current_title || 'Application'} — ${app.stage.replace(/_/g, ' ')}`,
            subtitle:     app.current_company || '',
            description:  `Pipeline stage advanced to ${app.stage}`,
            date:         fmtDate(app.updated_at || app.created_at),
            category:     'applications',
            priority:     'normal',
            status:       app.stage,
            jobId:        app.job_id,
            applicationId: app.id,
            entityId:     app.id,
            icon:         'trending-up',
            color:        '#8B5CF6',
            metadata:     app,
          });
        }
      }
    } catch (e) { console.warn('[AllActivity] applications:', e?.message); }

    // ── 2. INTERVIEWS ─────────────────────────────────────────────────
    // application_interviews is JOINed via application_id → applications.id
    // We fetch by candidate_email through applications; cleaner than guessing.
    try {
      const url = `/api/data/application_interviews?limit=50&sort=scheduled_date&order=desc`;
      const r = await apiFetch(url);
      const rows = Array.isArray(r) ? r : (r?.data || []);
      for (const iv of rows) {
        // Filter to interviews for THIS candidate by joining via application
        // candidate_email. The generic /api/data handler returns all rows
        // for the company; we trim client-side.
        if (iv.candidate_email && userEmail
            && String(iv.candidate_email).toLowerCase() !== String(userEmail).toLowerCase()) continue;

        const isCompleted = String(iv.status || '').toLowerCase() === 'completed';
        activities.push({
          id:           `db_iv_${iv.id}`,
          type:         isCompleted ? 'interview_completed' : 'interview_scheduled',
          title:        isCompleted
                          ? `${iv.interview_type || 'Interview'} completed`
                          : `${iv.interview_type || 'Interview'} scheduled`,
          subtitle:     iv.candidate_company || iv.employer_name || '',
          description:  iv.feedback || iv.notes
                          || `${iv.duration_minutes || 30} min · ${iv.location || iv.meeting_link || 'Remote'}`,
          date:         fmtDate(iv.scheduled_date || iv.created_at),
          category:     'interviews',
          priority:     isCompleted ? 'normal' : 'high',
          status:       iv.status || 'scheduled',
          jobId:        iv.job_id,
          interviewId:  iv.id,
          applicationId: iv.application_id,
          entityId:     iv.id,
          icon:         isCompleted ? 'check-circle' : 'calendar',
          color:        isCompleted ? '#10B981' : '#F59E0B',
          metadata:     iv,
        });
      }
    } catch (e) { console.warn('[AllActivity] interviews:', e?.message); }

    // ── 3. OFFERS ─────────────────────────────────────────────────────
    try {
      const queries = [];
      if (userId)    queries.push(`/api/data/job_offers?user_id=${userId}&limit=50`);
      if (userEmail) queries.push(`/api/data/job_offers?candidate_email=${encodeURIComponent(userEmail)}&limit=50`);
      const results = await Promise.all(queries.map(q => apiFetch(q).catch(() => ({ data: [] }))));
      const seenOfferIds = new Set();
      for (const r of results) {
        const rows = Array.isArray(r) ? r : (r?.data || []);
        for (const o of rows) {
          if (!o?.id || seenOfferIds.has(o.id)) continue;
          seenOfferIds.add(o.id);
          const s = String(o.status || '').toLowerCase();
          const type = s === 'accepted' ? 'offer_accepted'
                     : (s === 'rejected' || s === 'declined') ? 'offer_declined'
                     : 'offer_received';
          const isReceived = type === 'offer_received';
          activities.push({
            id:           `db_offer_${o.id}`,
            type,
            title:        type === 'offer_accepted'  ? `Offer accepted: ${o.position || 'role'}`
                        : type === 'offer_declined'  ? `Offer declined: ${o.position || 'role'}`
                        : `Offer received: ${o.position || 'role'}`,
            subtitle:     o.employer_name || o.company || '',
            description:  o.offered_salary
                          ? `${o.salary_currency === 'GBP' ? '£' : ''}${Number(o.offered_salary).toLocaleString()} · ${o.location || 'TBC'}`
                          : (o.salary ? `${o.salary}` : ''),
            date:         fmtDate(o.accepted_date || o.declined_date || o.offer_date || o.created_at),
            category:     'offers',
            priority:     isReceived ? 'high' : 'normal',
            status:       o.status || 'pending',
            jobId:        o.job_id,
            offerId:      o.id,
            entityId:     o.id,
            icon:         type === 'offer_accepted' ? 'check-decagram'
                        : type === 'offer_declined' ? 'close-circle' : 'gift',
            color:        type === 'offer_accepted' ? '#10B981'
                        : type === 'offer_declined' ? '#EF4444' : '#0e4e96',
            metadata:     o,
          });
        }
      }
    } catch (e) { console.warn('[AllActivity] offers:', e?.message); }

    // ── 4. SAVED JOBS ────────────────────────────────────────────────
    try {
      const url = userId
        ? `/api/data/saved_jobs?user_id=${userId}&limit=30`
        : `/api/data/saved_jobs?limit=30`;
      const r = await apiFetch(url);
      const rows = Array.isArray(r) ? r : (r?.data || []);
      for (const sj of rows) {
        activities.push({
          id:           `db_saved_${sj.id}`,
          type:         'job_saved',
          title:        `Saved: ${sj.title || sj.position || 'a job'}`,
          subtitle:     sj.company || sj.employer_name || '',
          description:  sj.location || '',
          date:         fmtDate(sj.created_at || sj.saved_at),
          category:     'jobs',
          priority:     'low',
          status:       'saved',
          jobId:        sj.job_id,
          entityId:     sj.id,
          icon:         'bookmark',
          color:        '#6366F1',
          metadata:     sj,
        });
      }
    } catch (e) { console.warn('[AllActivity] saved_jobs:', e?.message); }

    console.log(`[AllActivity] DB activities: ${activities.length}`);
    return activities;
  };

  // Sort activities based on selected option
  const sortActivities = (activitiesArray, sortOption) => {
    const sorted = [...activitiesArray];
    
    switch (sortOption) {
      case 'newest':
        return sorted.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      case 'oldest':
        return sorted.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      case 'priority':
        const priorityOrder = { high: 3, medium: 2, low: 1 };
        return sorted.sort((a, b) => priorityOrder[b.priority] - priorityOrder[a.priority]);
      case 'category':
        return sorted.sort((a, b) => a.category.localeCompare(b.category));
      default:
        return sorted;
    }
  };

  // Start entrance animations
  const startAnimations = () => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 800,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 800,
        useNativeDriver: true,
      }),
    ]).start();
  };

  // Handle refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    if (initializationError) {
      // If there was an initialization error, try to reinitialize
      await initializeData();
    } else {
      await loadActivities();
    }
    setRefreshing(false);
  };

  // Handle search
  const handleSearch = (query) => {
    setSearchQuery(query);
    applyFilters(query, selectedCategory, selectedStatus, selectedPriority, selectedDateRange);
  };

  // Apply filters
  const applyFilters = (search, category, status, priority, dateRange) => {
    try {
      const filters = {
        search,
        category: category !== 'all' ? category : null,
        status: status !== 'all' ? status : null,
        priority: priority !== 'all' ? priority : null,
        dateRange: dateRange !== 'all' ? dateRange : null
      };

      const filtered = activityTracker.getActivities(filters);
      const sorted = sortActivities(filtered, sortBy);
      setFilteredActivities(sorted);
    } catch (error) {
      console.error('❌ Error applying filters:', error);
    }
  };

  // Handle category filter
  const handleCategoryFilter = (category) => {
    setSelectedCategory(category);
    applyFilters(searchQuery, category, selectedStatus, selectedPriority, selectedDateRange);
  };

  // Handle status filter
  const handleStatusFilter = (status) => {
    setSelectedStatus(status);
    applyFilters(searchQuery, selectedCategory, status, selectedPriority, selectedDateRange);
  };

  // Handle priority filter
  const handlePriorityFilter = (priority) => {
    setSelectedPriority(priority);
    applyFilters(searchQuery, selectedCategory, selectedStatus, priority, selectedDateRange);
  };

  // Handle date range filter
  const handleDateRangeFilter = (dateRange) => {
    setSelectedDateRange(dateRange);
    applyFilters(searchQuery, selectedCategory, selectedStatus, selectedPriority, dateRange);
  };

  // Handle sort change
  const handleSortChange = (sortOption) => {
    setSortBy(sortOption);
    const sorted = sortActivities(filteredActivities, sortOption);
    setFilteredActivities(sorted);
  };

  // Navigate to activity detail
  const navigateToActivityDetail = (activity) => {
    try {
      console.log('🧭 Navigating to ActivityDetail:', activity.id);
      
      navigation.navigate('ActivityDetailScreen', {
        activityId: activity.id,
        activity: activity,
        source: 'AllActivityScreen'
      });
    } catch (error) {
      console.error('❌ Navigation error:', error);
      Alert.alert('Navigation Error', 'Unable to open activity details. Please try again.');
    }
  };

  // Navigate to related screen based on activity type — uses the real DB
  // ids (offerId, applicationId, interviewId, jobId) carried on each
  // activity row, so we never accidentally route to a mock 'job_002'.
  const navigateToRelatedScreen = (activity) => {
    try {
      console.log('🧭 Navigating to related screen for:', activity.type, 'id:', activity.entityId);

      switch (activity.type) {
        case 'application_submitted':
        case 'application_rejected':
        case 'application_status_change':
          navigation.navigate('JobApplicationsScreen', {
            highlightApplication: activity.applicationId,
            jobId: activity.jobId,
            source: 'AllActivityScreen',
          });
          break;

        case 'interview_scheduled':
        case 'interview_completed':
          navigation.navigate('InterviewHistoryScreen', {
            highlightInterview: activity.interviewId,
            applicationId: activity.applicationId,
            source: 'AllActivityScreen',
          });
          break;

        case 'offer_received':
        case 'offer_accepted':
        case 'offer_declined':
          // Real DB offer — pass the offerId so OfferDetails fetches the
          // canonical row instead of relying on route-param mock data.
          // Stripping the 'db_offer_' prefix isn't needed since we stored
          // entityId as the raw numeric offer id.
          if (activity.offerId || activity.entityId) {
            navigation.navigate('OfferDetails', {
              offerId: activity.offerId || activity.entityId,
              offer: activity.metadata,
              source: 'AllActivityScreen',
            });
          } else {
            navigation.navigate('JobsOffersScreen', { source: 'AllActivityScreen' });
          }
          break;

        case 'job_saved':
          navigation.navigate('SavedJobsScreen', {
            highlightJob: activity.jobId,
            source: 'AllActivityScreen',
          });
          break;

        case 'portfolio_updated':
          navigation.navigate('PortfolioScreen', { source: 'AllActivityScreen' });
          break;

        case 'skill_assessment':
        case 'course_completed':
          navigation.navigate('TestsInterviewsScreen', { source: 'AllActivityScreen' });
          break;

        case 'webinar_registered':
          navigation.navigate('WebinarRegistrationScreen', { source: 'AllActivityScreen' });
          break;

        default:
          navigateToActivityDetail(activity);
          break;
      }
    } catch (error) {
      console.error('❌ Navigation error:', error);
      navigateToActivityDetail(activity);
    }
  };

  // Get activity icon
  const getActivityIcon = (activity) => {
    const iconMap = {
      'application_submitted':     'paper-plane',
      'application_rejected':      'close-circle',
      'application_status_change': 'trending-up',
      'interview_scheduled':       'calendar',
      'interview_completed':       'checkmark-circle',
      'offer_received':            'gift',
      'offer_accepted':            'ribbon',
      'offer_declined':            'close-circle',
      'job_saved':                 'bookmark',
      'portfolio_updated':         'briefcase',
      'skill_assessment':          'school',
      'course_completed':          'book',
      'webinar_registered':        'videocam',
    };
    return iconMap[activity.type] || activity.icon || 'information-circle';
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    const colors = {
      high: '#EF4444',
      medium: '#F59E0B',
      low: '#10B981'
    };
    return colors[priority] || '#6B7280';
  };

  // Get status color
  const getStatusColor = (status) => {
    const colors = {
      completed: '#10B981',
      pending: '#F59E0B',
      scheduled: '#3B82F6',
      cancelled: '#EF4444'
    };
    return colors[status] || '#6B7280';
  };

  // Render activity item
  const renderActivityItem = ({ item, index }) => (
    <Animated.View
      style={[
        styles.activityCard,
        {
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
        },
      ]}
    >
      <TouchableOpacity
        style={styles.activityContent}
        onPress={() => navigateToActivityDetail(item)}
        onLongPress={() => navigateToRelatedScreen(item)}
        activeOpacity={0.7}
      >
        {/* Activity Icon and Priority Indicator */}
        <View style={styles.activityIconContainer}>
          <View style={[styles.activityIcon, { backgroundColor: item.color || '#6B7280' }]}>
            <Ionicons name={getActivityIcon(item)} size={20} color="#FFFFFF" />
          </View>
          <View style={[styles.priorityIndicator, { backgroundColor: getPriorityColor(item.priority) }]} />
        </View>

        {/* Activity Content */}
        <View style={styles.activityDetails}>
          <View style={styles.activityHeader}>
            <Text style={styles.activityTitle} numberOfLines={1}>
              {item.title}
            </Text>
            <View style={styles.activityMeta}>
              <Text style={styles.activityTime}>{item.time}</Text>
              <View style={[styles.statusBadge, { backgroundColor: getStatusColor(item.status) }]}>
                <Text style={styles.statusText}>{item.status}</Text>
              </View>
            </View>
          </View>

          <Text style={styles.activitySubtitle} numberOfLines={1}>
            {item.subtitle}
          </Text>

          {item.company && (
            <View style={styles.companyInfo}>
              {item.metadata?.companyLogo && (
                <Image source={{ uri: (item.metadata.companyLogo && typeof item.metadata.companyLogo=="string" && item.metadata.companyLogo.startsWith("http")) ? item.metadata.companyLogo : undefined }} style={styles.companyLogo} />
              )}
              <Text style={styles.companyName} numberOfLines={1}>
                {item.company}
              </Text>
            </View>
          )}

          <Text style={styles.activityDescription} numberOfLines={2}>
            {item.description}
          </Text>

          {/* Activity Tags */}
          <View style={styles.activityTags}>
            <View style={[styles.categoryTag, { backgroundColor: item.color || '#6B7280' }]}>
              <Text style={styles.categoryTagText}>{item.category}</Text>
            </View>
            <Text style={styles.activityDate}>{item.date}</Text>
          </View>
        </View>

        {/* Action Button */}
        <TouchableOpacity
          style={styles.actionButton}
          onPress={() => navigateToRelatedScreen(item)}
        >
          <Ionicons name="chevron-forward" size={20} color="#6B7280" />
        </TouchableOpacity>
      </TouchableOpacity>
    </Animated.View>
  );

  // Render statistics card
  const renderStatisticsCard = () => (
    <View style={styles.statisticsCard}>
      <Text style={styles.statisticsTitle}>Activity Overview</Text>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.statsScroll}>
        <View style={styles.statItem}>
          <Text style={styles.statValue}>{statistics.total || 0}</Text>
          <Text style={styles.statLabel}>Total</Text>
        </View>
        <View style={styles.statItem}>
          <Text style={styles.statValue}>{statistics.recent || 0}</Text>
          <Text style={styles.statLabel}>This Week</Text>
        </View>
        <View style={styles.statItem}>
          <Text style={styles.statValue}>{statistics.categories?.application || 0}</Text>
          <Text style={styles.statLabel}>Applications</Text>
        </View>
        <View style={styles.statItem}>
          <Text style={styles.statValue}>{statistics.categories?.interview || 0}</Text>
          <Text style={styles.statLabel}>Interviews</Text>
        </View>
        <View style={styles.statItem}>
          <Text style={styles.statValue}>{statistics.categories?.offer || 0}</Text>
          <Text style={styles.statLabel}>Offers</Text>
        </View>
      </ScrollView>
    </View>
  );

  // Render category filters
  const renderCategoryFilters = () => (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      style={styles.filtersScroll}
      contentContainerStyle={{ alignItems: 'center', paddingRight: 16 }}
    >
      {categories.map((category) => (
        <TouchableOpacity
          key={category.key}
          style={[
            styles.filterChip,
            selectedCategory === category.key && styles.filterChipActive,
            { borderColor: category.color }
          ]}
          onPress={() => handleCategoryFilter(category.key)}
        >
          <Ionicons 
            name={category.icon} 
            size={16} 
            color={selectedCategory === category.key ? '#FFFFFF' : category.color} 
          />
          <Text
            style={[
              styles.filterChipText,
              selectedCategory === category.key && styles.filterChipTextActive
            ]}
          >
            {category.label}
          </Text>
        </TouchableOpacity>
      ))}
    </ScrollView>
  );

  // Render empty state
  const renderEmptyState = () => (
    <View style={styles.emptyState}>
      {initializationError ? (
        <>
          <Ionicons name="alert-circle" size={64} color="#EF4444" />
          <Text style={styles.emptyStateTitle}>Initialization Error</Text>
          <Text style={styles.emptyStateText}>
            There was an issue loading your activities. Please try refreshing or restarting the app.
          </Text>
          <TouchableOpacity
            style={styles.retryButton}
            onPress={initializeData}
          >
            <Text style={styles.retryButtonText}>Retry</Text>
          </TouchableOpacity>
        </>
      ) : (
        <>
          <Ionicons name="document-text-outline" size={64} color="#9CA3AF" />
          <Text style={styles.emptyStateTitle}>No Activities Found</Text>
          <Text style={styles.emptyStateText}>
            {searchQuery || selectedCategory !== 'all' || selectedStatus !== 'all'
              ? 'Try adjusting your filters to see more activities.'
              : 'Your activities will appear here as you use the app.'}
          </Text>
          {(searchQuery || selectedCategory !== 'all' || selectedStatus !== 'all') && (
            <TouchableOpacity
              style={styles.clearFiltersButton}
              onPress={() => {
                setSearchQuery('');
                setSelectedCategory('all');
                setSelectedStatus('all');
                setSelectedPriority('all');
                setSelectedDateRange('all');
                applyFilters('', 'all', 'all', 'all', 'all');
              }}
            >
              <Text style={styles.clearFiltersText}>Clear Filters</Text>
            </TouchableOpacity>
          )}
        </>
      )}
    </View>
  );

  // Render filter modal
  const renderFilterModal = () => (
    <Modal
      visible={showFilters}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={() => setShowFilters(false)}
    >
      <SafeAreaView style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <Text style={styles.modalTitle}>Filter Activities</Text>
          <TouchableOpacity
            style={styles.modalCloseButton}
            onPress={() => setShowFilters(false)}
          >
            <Ionicons name="close" size={24} color="#374151" />
          </TouchableOpacity>
        </View>

        <ScrollView style={styles.modalContent}>
          {/* Status Filter */}
          <View style={styles.filterSection}>
            <Text style={styles.filterSectionTitle}>Status</Text>
            <View style={styles.filterOptions}>
              {statusOptions.map((option) => (
                <TouchableOpacity
                  key={option.key}
                  style={[
                    styles.filterOption,
                    selectedStatus === option.key && styles.filterOptionActive
                  ]}
                  onPress={() => handleStatusFilter(option.key)}
                >
                  <View style={[styles.filterOptionIndicator, { backgroundColor: option.color }]} />
                  <Text
                    style={[
                      styles.filterOptionText,
                      selectedStatus === option.key && styles.filterOptionTextActive
                    ]}
                  >
                    {option.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* Priority Filter */}
          <View style={styles.filterSection}>
            <Text style={styles.filterSectionTitle}>Priority</Text>
            <View style={styles.filterOptions}>
              {priorityOptions.map((option) => (
                <TouchableOpacity
                  key={option.key}
                  style={[
                    styles.filterOption,
                    selectedPriority === option.key && styles.filterOptionActive
                  ]}
                  onPress={() => handlePriorityFilter(option.key)}
                >
                  <View style={[styles.filterOptionIndicator, { backgroundColor: option.color }]} />
                  <Text
                    style={[
                      styles.filterOptionText,
                      selectedPriority === option.key && styles.filterOptionTextActive
                    ]}
                  >
                    {option.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* Date Range Filter */}
          <View style={styles.filterSection}>
            <Text style={styles.filterSectionTitle}>Date Range</Text>
            <View style={styles.filterOptions}>
              {dateRangeOptions.map((option) => (
                <TouchableOpacity
                  key={option.key}
                  style={[
                    styles.filterOption,
                    selectedDateRange === option.key && styles.filterOptionActive
                  ]}
                  onPress={() => handleDateRangeFilter(option.key)}
                >
                  <Text
                    style={[
                      styles.filterOptionText,
                      selectedDateRange === option.key && styles.filterOptionTextActive
                    ]}
                  >
                    {option.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* Sort Options */}
          <View style={styles.filterSection}>
            <Text style={styles.filterSectionTitle}>Sort By</Text>
            <View style={styles.filterOptions}>
              {sortOptions.map((option) => (
                <TouchableOpacity
                  key={option.key}
                  style={[
                    styles.filterOption,
                    sortBy === option.key && styles.filterOptionActive
                  ]}
                  onPress={() => handleSortChange(option.key)}
                >
                  <Text
                    style={[
                      styles.filterOptionText,
                      sortBy === option.key && styles.filterOptionTextActive
                    ]}
                  >
                    {option.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#0e4e96" />
          <Text style={styles.loadingText}>Loading activities...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />
      
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Ionicons name="arrow-back" size={24} color="#FFFFFF" />
        </TouchableOpacity>
        
        <JobengLogo />
        
        <View style={styles.headerActions}>
          <TouchableOpacity
            style={styles.headerButton}
            onPress={() => setShowFilters(true)}
          >
            <Ionicons name="options" size={24} color="#FFFFFF" />
          </TouchableOpacity>
        </View>
      </View>

      {/* Title */}
      <View style={styles.titleContainer}>
        <Text style={styles.screenTitle}>All Activities</Text>
        <Text style={styles.screenSubtitle}>Track your job search progress</Text>
      </View>

      {/* Search Bar */}
      <View style={styles.searchContainer}>
        <View style={styles.searchBar}>
          <Ionicons name="search" size={20} color="#9CA3AF" />
          <TextInput
            style={styles.searchInput}
            placeholder="Search activities..."
            placeholderTextColor="#9CA3AF"
            value={searchQuery}
            onChangeText={handleSearch}
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity onPress={() => handleSearch('')}>
              <Ionicons name="close-circle" size={20} color="#9CA3AF" />
            </TouchableOpacity>
          )}
        </View>
      </View>

      {/* Statistics Card */}
      {!initializationError && renderStatisticsCard()}

      {/* Category Filters */}
      {!initializationError && renderCategoryFilters()}

      {/* Activities List */}
      <FlatList
        data={filteredActivities}
        renderItem={renderActivityItem}
        keyExtractor={(item, index) => item.id ? item.id.toString() : `activity-${index}`}
        contentContainerStyle={styles.activitiesList}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleRefresh}
            colors={['#0e4e96']}
            tintColor="#0e4e96"
          />
        }
        ListEmptyComponent={renderEmptyState}
        initialNumToRender={10}
        maxToRenderPerBatch={10}
        windowSize={10}
        removeClippedSubviews={true}
      />

      {/* Filter Modal */}
      {renderFilterModal()}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8FAFC',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F8FAFC',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#6B7280',
    fontWeight: '500',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: '#0e4e96',
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  logoContainer: {
    flex: 1,
    alignItems: 'center',
  },
  logoText: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#FFFFFF',
    letterSpacing: 1,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 8,
  },
  titleContainer: {
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  screenTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: '#111827',
    marginBottom: 4,
  },
  screenSubtitle: {
    fontSize: 16,
    color: '#6B7280',
    fontWeight: '400',
  },
  searchContainer: {
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: '#FFFFFF',
  },
  searchBar: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F3F4F6',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  searchInput: {
    flex: 1,
    marginLeft: 12,
    fontSize: 16,
    color: '#111827',
  },
  statisticsCard: {
    backgroundColor: '#FFFFFF',
    marginHorizontal: 20,
    marginVertical: 8,
    borderRadius: 16,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  statisticsTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 16,
  },
  statsScroll: {
    flexDirection: 'row',
  },
  statItem: {
    alignItems: 'center',
    marginRight: 24,
  },
  statValue: {
    fontSize: 24,
    fontWeight: '700',
    color: '#0e4e96',
    marginBottom: 4,
  },
  statLabel: {
    fontSize: 12,
    color: '#6B7280',
    fontWeight: '500',
  },
  filtersScroll: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    backgroundColor: '#FFFFFF',
    // Pin height so the chip strip is exactly the chip height + paddingVertical*2.
    // Without this, switching tabs causes a one-frame paint where the strip
    // re-measures and visually "pops" before settling.
    height: 58,
  },
  filterChip: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 14,
    paddingVertical: 0,
    borderRadius: 20,
    borderWidth: 1.5,
    marginRight: 10,
    backgroundColor: '#FFFFFF',
    // Pin both axes so switching active/inactive never reflows the strip:
    //   minWidth covers "Offers" / "Learning" (shortest labels)
    //   height pins the vertical box exactly so border/padding can't ripple
    minWidth: 110,
    height: 38,
  },
  filterChipActive: {
    backgroundColor: '#0e4e96',
    borderColor: '#0e4e96',
  },
  filterChipText: {
    marginLeft: 6,
    fontSize: 13,
    fontWeight: '600',
    color: '#6B7280',
  },
  filterChipTextActive: {
    // ONLY override color — fontWeight stays at 600 so the label width
    // doesn't shift between active and inactive states (a heavier
    // fontWeight would render slightly wider and nudge surrounding chips).
    color: '#FFFFFF',
  },
  activitiesList: {
    paddingHorizontal: 20,
    paddingBottom: 20,
  },
  activityCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    marginVertical: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  activityContent: {
    flexDirection: 'row',
    padding: 16,
    alignItems: 'flex-start',
  },
  activityIconContainer: {
    position: 'relative',
    marginRight: 16,
  },
  activityIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  priorityIndicator: {
    position: 'absolute',
    top: -2,
    right: -2,
    width: 12,
    height: 12,
    borderRadius: 6,
    borderWidth: 2,
    borderColor: '#FFFFFF',
  },
  activityDetails: {
    flex: 1,
  },
  activityHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 4,
  },
  activityTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
    flex: 1,
    marginRight: 8,
  },
  activityMeta: {
    alignItems: 'flex-end',
  },
  activityTime: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 4,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 8,
  },
  statusText: {
    fontSize: 10,
    color: '#FFFFFF',
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  activitySubtitle: {
    fontSize: 14,
    color: '#6B7280',
    fontWeight: '500',
    marginBottom: 8,
  },
  companyInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  companyLogo: {
    width: 20,
    height: 20,
    borderRadius: 10,
    marginRight: 8,
  },
  companyName: {
    fontSize: 14,
    color: '#374151',
    fontWeight: '500',
  },
  activityDescription: {
    fontSize: 14,
    color: '#6B7280',
    lineHeight: 20,
    marginBottom: 12,
  },
  activityTags: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  categoryTag: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
  },
  categoryTagText: {
    fontSize: 10,
    color: '#FFFFFF',
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  activityDate: {
    fontSize: 12,
    color: '#9CA3AF',
    fontWeight: '500',
  },
  actionButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#F3F4F6',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 12,
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 40,
    paddingVertical: 60,
  },
  emptyStateTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#374151',
    marginTop: 16,
    marginBottom: 8,
  },
  emptyStateText: {
    fontSize: 16,
    color: '#6B7280',
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: 24,
  },
  clearFiltersButton: {
    backgroundColor: '#0e4e96',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  clearFiltersText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  retryButton: {
    backgroundColor: '#EF4444',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  retryButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  modalContainer: {
    flex: 1,
    backgroundColor: '#F8FAFC',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#111827',
  },
  modalCloseButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#F3F4F6',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    flex: 1,
    paddingHorizontal: 20,
  },
  filterSection: {
    marginVertical: 16,
  },
  filterSectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 12,
  },
  filterOptions: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    overflow: 'hidden',
  },
  filterOption: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
  },
  filterOptionActive: {
    backgroundColor: '#EBF4FF',
  },
  filterOptionIndicator: {
    width: 12,
    height: 12,
    borderRadius: 6,
    marginRight: 12,
  },
  filterOptionText: {
    fontSize: 16,
    color: '#374151',
    fontWeight: '500',
  },
  filterOptionTextActive: {
    color: '#0e4e96',
    fontWeight: '600',
  },
});
